import React, { useCallback, useEffect, useState } from 'react';
import SplitPane, { Pane, SashContent } from 'split-pane-react';
import NodePropertyPane from '../NodePropertyPane/NodePropertyPane';
import NodeProvider from '../NodeProvider/NodeProvider';
import NodeEditor from '../NodeEditor/NodeEditor';
import LogsPane from '../LogsPane/LogsPane';
import SaveAsDialog from './SaveAsDialog';
import 'split-pane-react/esm/themes/default.css';
import { Node, App, NodeEvent, EngineEvent, ProjectEvent, Point, Project, ErrorEvent, Port } from '../peregrineFlowInterfaces';
import { socket } from '../socket';
import { enqueueSnackbar } from 'notistack';
import useStore, { nodeToCustomReactFlowNode } from '../store';

import './editor-window.css';
import { Edge, addEdge } from 'reactflow';
import { QuickNodeAdd } from '../NodeEditor/QuickNodeAdd';
import { useEditorKeyboard } from './useEditorKeyboard';
import { OnFlowReady } from '../NodeEditor/NodeEditor';

type EditorWindowProps = {
    project: Project
}

const EditorWindow = ({ project: initialProject }: EditorWindowProps) => {
    const [sizesMainPane, setSizesMainPane] = useState<(number | string)[]>(['auto', 40]);
    const [sizesTopPane, setSizesTopPane] = useState<(number | string)[]>(['auto', 400]);
    const [availableNodes, setAvailableNodes] = useState<Array<Node>>([]);
    const [potentialNodePosition, setPotentialNodePosition] = useState<Point>({ x: 0, y: 0 });
    const [logsExpanded, setLogsExpanded] = useState(false);
    const [prevLogSize, setPrevLogSize] = useState<string | number>(200);
    const [isAuthenticated] = useStore((state) => [state.isAuthenticated]);

    const [pendingConnection, setPendingConnection] = useState<{ nodeId: string, handleId: string, handleType: string } | null>(null);
    const [filterInputTypes, setFilterInputTypes] = useState<string[]>([]);
    const [filterOutputTypes, setFilterOutputTypes] = useState<string[]>([]);
    const [showQuickAdd, setShowQuickAdd] = useState(false);
    const [quickAddPosition, setQuickAddPosition] = useState<Point>({ x: 0, y: 0 });

    // Store state
    const [project, setProject] = useStore((state) => [state.project, state.setProject]);
    const [nodes, addNode] = useStore((state) => [state.nodes, state.addNode]);
    const edges = useStore((state) => state.edges);
    const setEdges = useStore((state) => state.setEdges);
    const setIsRunning = useStore((state) => state.setIsRunning);
    const setLockEngine = useStore((state) => state.setLockEngine);
    const isGuest = useStore((state) => state.isGuest);
    const setShowSignUpDialog = useStore((state) => state.setShowSignUpDialog);

    // Store-based dialog states
    const showNodeProvider = useStore((state) => state.showNodeProvider);
    const setShowNodeProvider = useStore((state) => state.setShowNodeProvider);
    const showSaveAsDialog = useStore((state) => state.showSaveAsDialog);
    const setShowSaveAsDialog = useStore((state) => state.setShowSaveAsDialog);

    const projectToFlowRef = React.useRef<((pos: { x: number; y: number }) => { x: number; y: number }) | null>(null);
    const onFlowReady = React.useCallback<OnFlowReady>((fn) => { projectToFlowRef.current = fn; }, []);
    const getProjectToFlow = React.useCallback(
        (pos: { x: number; y: number }) => projectToFlowRef.current?.(pos) ?? pos,
        []
    );

    const { pushHistory } = useEditorKeyboard(getProjectToFlow);

    // Push a history snapshot whenever nodes or edges are added/removed
    const prevNodeLenRef = React.useRef(nodes.length);
    const prevEdgeLenRef = React.useRef(edges.length);
    useEffect(() => {
        if (nodes.length !== prevNodeLenRef.current || edges.length !== prevEdgeLenRef.current) {
            prevNodeLenRef.current = nodes.length;
            prevEdgeLenRef.current = edges.length;
            pushHistory();
        }
    }, [nodes.length, edges.length, pushHistory]);

    const sashRenderer = (_: any, active: boolean) => {
        return <SashContent active={active} />;
    };

    const loadProject = useCallback((projectToLoad: Project) => {
        setProject(projectToLoad);
        // Reset engine running state when loading a project
        setIsRunning(false);
        setLockEngine(false);

        if (projectToLoad.nodes_to_load !== null) {
            Object.values(projectToLoad.nodes_to_load ?? {}).forEach((node) => {
                const n = nodeToCustomReactFlowNode(node);
                n.position = node.position as Point;
                addNode(n);
            });

            if (projectToLoad.extra_data) {
                const loadedEdges = projectToLoad.extra_data?.edges.filter((edge: Edge) => {
                    if (projectToLoad.nodes_to_load?.hasOwnProperty(edge.source) && projectToLoad.nodes_to_load?.hasOwnProperty(edge.target)) {
                        return true;
                    }
                    return false;
                });
                setEdges(loadedEdges);
            }
        }
    }, [addNode, setEdges, setProject, setIsRunning, setLockEngine]);

    // Load the initial project when the component mounts
    useEffect(() => {
        if (initialProject) {
            loadProject(initialProject);
        }
    }, [initialProject, loadProject]);

    // Setup socket listeners
    useEffect(() => {
        socket.emit('setup');

        const onSetup = (data: App) => {
            setAvailableNodes(data.available_nodes);
        };
        const onError = (data: ErrorEvent) => {
            enqueueSnackbar(data.message, { variant: "error" });
        };

        socket.on('setup', onSetup);
        socket.on('error', onError);

        return () => {
            socket.off('setup', onSetup);
            socket.off('error', onError);
        };
    }, [isAuthenticated]);

    // Engine state listener
    useEffect(() => {
        const onEngineEvent = (event: EngineEvent) => {
            if (event.event_type === "running") {
                setIsRunning(true);
                setLockEngine(false);
            } else if (event.event_type === "stopped") {
                setIsRunning(false);
                setLockEngine(false);
            } else {
                setLockEngine(true);
            }
        };

        socket.on('engine', onEngineEvent);
        return () => {
            socket.off('engine', onEngineEvent);
        };
    }, [setIsRunning, setLockEngine]);

    // Auto-save project on changes
    useEffect(() => {
        const timeOutId = setTimeout(() => {
            updateProject(false);
        }, 500);
        return () => clearTimeout(timeOutId);
    }, [edges, nodes]); // eslint-disable-line react-hooks/exhaustive-deps

    const onContextMenu = useCallback((e: React.MouseEvent<HTMLDivElement>, pos: Point) => {
        setShowNodeProvider(true);
        setPotentialNodePosition(pos);
        setPendingConnection(null);
        setFilterInputTypes([]);
        setFilterOutputTypes([]);
        setShowQuickAdd(false);
    }, [setShowNodeProvider]);

    const onConnectionDrop = useCallback((pos: Point, screenPos: Point, node: Node, handleId: string | null, handleType: string | null) => {
        if (!handleId || !handleType) return;

        // Extract index from handleId (format: input-{nodeId}-port-id-{index})
        const parts = handleId.split('-');
        const portIndex = parseInt(parts[parts.length - 1]);

        // If dragging from an input, check if it's already connected
        // Each input can only have one connection
        if (handleType === 'target') {
            const isAlreadyConnected = edges.some(
                edge => edge.target === node.id && edge.targetHandle === handleId
            );
            if (isAlreadyConnected) {
                // Input already has a connection, don't allow another
                return;
            }
        }

        setPotentialNodePosition(pos);
        setQuickAddPosition(screenPos);
        setPendingConnection({ nodeId: node.id, handleId, handleType });

        // Find the port data type
        let dataTypes: string[] = [];

        if (handleType === 'source') {
            // Dragged from output, so we look for compatible INPUTs
            const port = node.output_ports[portIndex];
            if (port) {
                dataTypes = Array.isArray(port.data_type) ? port.data_type : [port.data_type];
                setFilterInputTypes(dataTypes);
                setFilterOutputTypes([]);
            }
        } else {
            // Dragged from input, so we look for compatible OUTPUTs
            const port = node.input_ports[portIndex];
            if (port) {
                dataTypes = Array.isArray(port.data_type) ? port.data_type : [port.data_type];
                setFilterOutputTypes(dataTypes);
                setFilterInputTypes([]);
            }
        }

        setShowQuickAdd(true);
    }, [edges]);

    const onQuickAddClose = useCallback(() => {
        setShowQuickAdd(false);
        setPendingConnection(null);
        setFilterInputTypes([]);
        setFilterOutputTypes([]);
    }, []);

    const updateProject = useCallback((save: boolean) => {
        if (project?.read_only) {
            return;
        }

        const layout = Object.fromEntries(nodes.map((node) => {
            const pos = { x: node.position.x, y: node.position.y } as Point;
            return [node.id, pos];
        }));

        if (project) {
            project.extra_data = { layout: layout, edges: edges };
        }
        const event = { event_type: "update", project: project, save: save } as unknown as ProjectEvent;
        socket.emit("project", event);
    }, [project, nodes, edges]);

    const saveProjectAs = useCallback((newProjectName: string) => {
        if (!project) {
            return;
        }

        project.name = newProjectName;
        project.read_only = false;
        updateProject(true);
        setShowSaveAsDialog(false);
    }, [project, updateProject, setShowSaveAsDialog]);

    const onNodeProvideNodeSelected = useCallback((node: Node) => {
        // Check if guest user is trying to add a premium node
        if (isGuest && node.is_premium) {
            setShowSignUpDialog(true, 'premiumNode');
            return;
        }

        const n = nodeToCustomReactFlowNode(node);
        n.position = { ...potentialNodePosition };

        const event = { event_type: "add", node: n.data } as unknown as NodeEvent;
        socket.emit("node", event);
        addNode(n);
        pushHistory();

        if (pendingConnection) {
            let sourceId: string | undefined, sourceHandle: string | undefined, targetId: string | undefined, targetHandle: string | undefined;

            if (pendingConnection.handleType === 'source') {
                sourceId = pendingConnection.nodeId;
                sourceHandle = pendingConnection.handleId;
                targetId = n.id;

                // Find compatible input port on new node
                const nodeData = n.data as unknown as Node;
                const compatiblePortIndex = nodeData.input_ports.findIndex((p: Port) => {
                    const pTypes = Array.isArray(p.data_type) ? p.data_type : [p.data_type];
                    if (pTypes.includes('Any')) return true;
                    return pTypes.some((t: string) => filterInputTypes.includes(t));
                });

                if (compatiblePortIndex !== -1) {
                    targetHandle = `input-${n.id}-port-id-${compatiblePortIndex}`;
                }
            } else {
                targetId = pendingConnection.nodeId;
                targetHandle = pendingConnection.handleId;
                sourceId = n.id;

                // Find compatible output port on new node
                const nodeData = n.data as unknown as Node;
                const compatiblePortIndex = nodeData.output_ports.findIndex((p: Port) => {
                    const pTypes = Array.isArray(p.data_type) ? p.data_type : [p.data_type];
                    if (filterOutputTypes.includes('Any')) return true;
                    if (pTypes.includes('Any')) return true;
                    return pTypes.some((t: string) => filterOutputTypes.includes(t));
                });

                if (compatiblePortIndex !== -1) {
                    sourceHandle = `output-${n.id}-port-id-${compatiblePortIndex}`;
                }
            }

            if (sourceId && sourceHandle && targetId && targetHandle) {
                const newEdge = {
                    id: `e${sourceId}${sourceHandle}-${targetId}${targetHandle}`,
                    source: sourceId,
                    sourceHandle: sourceHandle,
                    target: targetId,
                    targetHandle: targetHandle
                };
                setEdges(addEdge(newEdge, edges));

                // Emit port_connect event to backend for auto-detection support
                // Use setTimeout to ensure the node creation event is processed first
                const providerNode = sourceId === n.id ? n.data : nodes.find(nd => nd.id === sourceId)?.data;
                const consumerNode = targetId === n.id ? n.data : nodes.find(nd => nd.id === targetId)?.data;

                if (providerNode && consumerNode) {
                    setTimeout(() => {
                        socket.emit('port_connect', {
                            event_type: 'connect',
                            provider_node: providerNode as unknown as Node,
                            provider_port: sourceHandle,
                            consumer_node: consumerNode as unknown as Node,
                            consumer_port: targetHandle,
                        });
                    }, 100);
                }
            }

            setPendingConnection(null);
            setFilterInputTypes([]);
            setFilterOutputTypes([]);
            setShowQuickAdd(false);
        }
    }, [potentialNodePosition, addNode, nodes, pendingConnection, filterInputTypes, filterOutputTypes, setEdges, edges, isGuest, setShowSignUpDialog, pushHistory]);

    const renderNodeEditor = useCallback(() => {
        return (
            <div style={{ width: '100%', height: '100%', position: 'relative' }}>
                <NodeProvider
                    availableNodes={availableNodes}
                    visible={showNodeProvider}
                    onSelectNode={onNodeProvideNodeSelected}
                    onClose={() => setShowNodeProvider(false)}
                    initialInputTypes={filterInputTypes}
                    initialOutputTypes={filterOutputTypes}
                />
                <QuickNodeAdd
                    visible={showQuickAdd}
                    position={quickAddPosition}
                    availableNodes={availableNodes}
                    filterInputTypes={filterInputTypes}
                    filterOutputTypes={filterOutputTypes}
                    onSelectNode={onNodeProvideNodeSelected}
                    onClose={onQuickAddClose}
                />
                <NodeEditor onContextMenu={onContextMenu} onConnectionDrop={onConnectionDrop} onFlowReady={onFlowReady} />
            </div>
        );
    }, [availableNodes, showNodeProvider, onContextMenu, onFlowReady, onNodeProvideNodeSelected, setShowNodeProvider, onConnectionDrop, filterInputTypes, filterOutputTypes, showQuickAdd, quickAddPosition, onQuickAddClose]);

    const toggleLogs = useCallback(() => {
        if (logsExpanded) {
            // Collapse
            const currentSize = sizesMainPane[1];
            if (currentSize !== 40 && currentSize !== '40px') {
                setPrevLogSize(currentSize);
            }
            setSizesMainPane(['auto', 40]);
        } else {
            // Expand
            setSizesMainPane(['auto', prevLogSize]);
        }
        setLogsExpanded(!logsExpanded);
    }, [logsExpanded, sizesMainPane, prevLogSize]);

    return (
        <div style={{ height: '100%' }}>
            <SplitPane
                split='horizontal'
                sizes={sizesMainPane}
                onChange={setSizesMainPane}
                sashRender={sashRenderer}
            >
                <Pane style={{ width: "100%", height: "100%", position: "relative" }}>
                    <SplitPane
                        split='vertical'
                        sizes={sizesTopPane}
                        onChange={setSizesTopPane}
                        sashRender={sashRenderer}
                    >
                        <Pane minSize={200} style={{ width: '100%', height: '100%' }}>
                            {renderNodeEditor()}
                        </Pane>
                        <Pane minSize={300} maxSize={500} className='right-pane'>
                            <NodePropertyPane />
                        </Pane>
                    </SplitPane>
                </Pane>
                <Pane maxSize={logsExpanded ? '50%' : '40px'} minSize={'40px'} className='bottom-pane'>
                    <LogsPane expanded={logsExpanded} onToggleExpand={toggleLogs} />
                </Pane>
            </SplitPane>
            <SaveAsDialog
                open={showSaveAsDialog}
                onClose={() => setShowSaveAsDialog(false)}
                onSave={saveProjectAs}
            />
        </div>
    );
};

export default EditorWindow;
